import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  LueftungBrandschutzantwort, LueftungBskzeile, LueftungEntrauchungszeile, LueftungSeitenzeile,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, formatiereZahl, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../../muster';
import {
  AnnahmenAbschnitt, Elementverweis, NormAbschnitt, Rechenstand, konsistenzStatus, optionenAus, seitenVorbehalte,
} from './pruefung';

/** Seitenzeilen mit ihrer Stelle im Lauf - der stabile Schlüssel der Tabellen. */
type Bsk = LueftungSeitenzeile<LueftungBskzeile> & { nr: number };
type Ent = LueftungSeitenzeile<LueftungEntrauchungszeile> & { nr: number };

/** Die Befundcodes im Klartext - dieselben Sätze wie in PDF und Excel. */
const BSK_CODES: Record<string, string> = {
  ohne_aks: 'ohne AKS-Kürzel BSK',
  aks_abweichend: 'AKS-Kürzel weicht ab',
  ohne_msr: 'ohne Gegenstück in der Gebäudeautomation',
  ohne_anlage: 'ohne Anlage',
  msr_ohne_modell: 'nur in der Gebäudeautomation',
  ohne_endlagen: 'ohne Endlagen-Datenpunkte',
};

const ENT_CODES: Record<string, string> = {
  ohne_klappe_msr: 'Anlage ohne Klappe in der Gebäudeautomation',
  bauart_abweichend: 'Bauart weicht ab',
  ohne_anlage: 'ohne Anlage',
  ohne_msr: 'ohne Gegenstück in der Gebäudeautomation',
  an_nicht_ent_anlage: 'an einer Anlage, die keine Entrauchungsanlage ist',
  ohne_lueftungsmodell: 'ohne Gegenstück im Lüftungsmodell',
};

const EBENE: Record<string, string> = { anlage: 'Anlage', klappe: 'Klappe', zentrale: 'Zentrale' };

/** Befundcodes einer Zeile, getrennt und übersetzt. */
export function codes(befunde: string | null | undefined, texte: Record<string, string>): string[] {
  return (befunde ?? '').split(',').map((c) => c.trim()).filter(Boolean).map((c) => texte[c] ?? c);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRANDSCHUTZKLAPPEN UND ENTRAUCHUNG (TV5, Lauf lueftung-pruefen = J9)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Zwei KONSISTENZLISTEN: Ist dieselbe Klappe in Lüftungsmodell und
 * Gebäudeautomation dieselbe? Sie sind keine Bemessung - ob an einer Stelle eine
 * Klappe nötig ist, sagt das Brandschutzkonzept. Ein Befund ist deshalb eine
 * Warnung, nie rot.
 */
export function LueftungBrandschutz() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<LueftungBrandschutzantwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.lueftungBrandschutz(projekt.id)
      .then(setDaten)
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projekt.id]);

  useEffect(() => { laden(); }, [laden]);

  const bskZeilen = useMemo<Bsk[]>(() => (daten?.bskZeilen ?? []).map((z, nr) => ({ ...z, nr })), [daten]);
  const entZeilen = useMemo<Ent[]>(() => (daten?.entrauchungZeilen ?? []).map((z, nr) => ({ ...z, nr })), [daten]);

  // ── Filter in der Adresse, je Liste mit eigenem Vorsatz ──
  const bskFilter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'stand', beschriftung: 'Stand', art: 'auswahl', platzhalter: 'alle',
      optionen: [{ wert: 'befund', beschriftung: 'Befund' }, { wert: 'konsistent', beschriftung: 'konsistent' }],
    },
    {
      schluessel: 'befund', beschriftung: 'Befund', art: 'auswahl', platzhalter: 'alle',
      optionen: Object.entries(BSK_CODES).map(([wert, beschriftung]) => ({ wert, beschriftung })),
    },
    {
      schluessel: 'anlage', beschriftung: 'Anlage', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(bskZeilen.map((z) => z.zeile.anlage)),
    },
    {
      schluessel: 'baureihe', beschriftung: 'Baureihe', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(bskZeilen.map((z) => z.zeile.baureihe)),
    },
  ], [bskZeilen]);
  const [bskWerte, setBskWerte] = useFilterInUrl(bskFilter, 'bsk_');

  const entFilter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'ebene', beschriftung: 'Ebene', art: 'auswahl', platzhalter: 'alle',
      optionen: Object.entries(EBENE).map(([wert, beschriftung]) => ({ wert, beschriftung })),
    },
    {
      schluessel: 'stand', beschriftung: 'Stand', art: 'auswahl', platzhalter: 'alle',
      optionen: [
        { wert: 'befund', beschriftung: 'Befund' }, { wert: 'hinweis', beschriftung: 'Hinweis' },
        { wert: 'konsistent', beschriftung: 'konsistent' },
      ],
    },
    {
      schluessel: 'anlage', beschriftung: 'Anlage', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(entZeilen.map((z) => z.zeile.anlage)),
    },
  ], [entZeilen]);
  const [entWerte, setEntWerte] = useFilterInUrl(entFilter, 'ent_');

  const bskGefiltert = useMemo(() => bskZeilen.filter((z) => (!bskWerte.stand || z.zeile.stand === bskWerte.stand)
    && (!bskWerte.befund || (z.zeile.befunde ?? '').split(',').includes(bskWerte.befund as string))
    && (!bskWerte.anlage || z.zeile.anlage === bskWerte.anlage)
    && (!bskWerte.baureihe || z.zeile.baureihe === bskWerte.baureihe)), [bskZeilen, bskWerte]);

  const entGefiltert = useMemo(() => entZeilen.filter((z) => (!entWerte.ebene || z.zeile.ebene === entWerte.ebene)
    && (!entWerte.stand || z.zeile.stand === entWerte.stand)
    && (!entWerte.anlage || z.zeile.anlage === entWerte.anlage)), [entZeilen, entWerte]);

  const bskSpalten = useMemo<Spalte<Bsk>[]>(() => [
    { schluessel: 'element', titel: 'Element', immerSichtbar: true, wert: (z) => z.zeile.kennung },
    {
      schluessel: 'stand', titel: 'Stand', art: 'status', wert: (z) => z.zeile.stand,
      inhalt: (z) => <StatusBadge {...konsistenzStatus(z.zeile.stand)} klein />,
    },
    { schluessel: 'befunde', titel: 'Befunde', wert: (z) => codes(z.zeile.befunde, BSK_CODES).join('; ') },
    { schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.zeile.baureihe ?? '' },
    { schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.zeile.anlage ?? '' },
    { schluessel: 'ebene', titel: 'Ebene', wert: (z) => z.zeile.ebene ?? '' },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.zeile.raumnummer ?? '' },
    { schluessel: 'aks', titel: 'AKS', wert: (z) => z.zeile.aksCode ?? '' },
    { schluessel: 'msrAks', titel: 'MSR-AKS', wert: (z) => z.zeile.msrAks ?? '' },
    { schluessel: 'motor', titel: 'Motorantrieb', wert: (z) => z.zeile.motorantrieb ?? undefined },
    { schluessel: 'dp', titel: 'MSR-Datenpunkte', art: 'zahl', nachkomma: 0, wert: (z) => z.zeile.msrDatenpunkte },
    { schluessel: 'familie', titel: 'Familie', wert: (z) => z.zeile.familie ?? '', versteckt: true },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.zeile.typ ?? '', versteckt: true },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.zeile.text },
    {
      schluessel: 'verweis', titel: 'Verweis', sortierbar: false,
      inhalt: (z) => <Elementverweis projektId={projekt.id} ziel={z.ziel} kennung={z.zeile.kennung} ohne="nur in der Gebäudeautomation" />,
    },
  ], [projekt.id]);

  const entSpalten = useMemo<Spalte<Ent>[]>(() => [
    { schluessel: 'ebene', titel: 'Ebene', immerSichtbar: true, wert: (z) => EBENE[z.zeile.ebene] ?? z.zeile.ebene },
    { schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.zeile.anlage ?? '' },
    { schluessel: 'msr', titel: 'MSR-Kennung', wert: (z) => z.zeile.msrKennung ?? '' },
    { schluessel: 'element', titel: 'Element', wert: (z) => z.zeile.kennung ?? '' },
    {
      schluessel: 'stand', titel: 'Stand', art: 'status', wert: (z) => z.zeile.stand,
      inhalt: (z) => <StatusBadge {...konsistenzStatus(z.zeile.stand)} klein />,
    },
    { schluessel: 'befunde', titel: 'Befunde', wert: (z) => codes(z.zeile.befunde, ENT_CODES).join('; ') },
    { schluessel: 'klappenModell', titel: 'Klappen Modell', art: 'zahl', nachkomma: 0, wert: (z) => z.zeile.klappenModell },
    { schluessel: 'klappenMsr', titel: 'Klappen MSR', art: 'zahl', nachkomma: 0, wert: (z) => z.zeile.klappenMsr },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.zeile.typ ?? '', versteckt: true },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.zeile.text },
    {
      schluessel: 'verweis', titel: 'Verweis', sortierbar: false,
      inhalt: (z) => <Elementverweis projektId={projekt.id} ziel={z.ziel} kennung={z.zeile.kennung} />,
    },
  ], [projekt.id]);

  const ohneLauf = !!daten && !daten.lauf;
  const gesperrt = !daten?.lauf ? { grund: 'Noch kein Lauf lueftung-pruefen.' } : undefined;
  const bsk = daten?.bsk ?? null;
  const ent = daten?.entrauchung ?? null;

  return (
    <>
      <PageHeader
        titel="Brandschutzklappen und Entrauchung"
        beschreibung="BSK- und Entrauchungsliste: ob Klappen, Anlagen und RWA-Zentralen in Lüftungsmodell und Gebäudeautomation übereinstimmen."
        meta={<Rechenstand lauf={daten?.lauf} />}
        aktionen={(
          <ExportLeiste ziele={[
            { art: 'pdf', beschriftung: 'PDF LUE-BSK', gesperrt, laden: () => api.lueftungPruefberichtPdf(projekt.id, 'bsk') },
            { art: 'pdf', beschriftung: 'PDF LUE-ENR', gesperrt, laden: () => api.lueftungPruefberichtPdf(projekt.id, 'entrauchung') },
            { art: 'excel', beschriftung: 'Excel BSK', gesperrt, laden: () => api.lueftungPruefListen(projekt.id, 'pruefung', 'bsk') },
            { art: 'excel', beschriftung: 'Excel Entrauchung', gesperrt, laden: () => api.lueftungPruefListen(projekt.id, 'pruefung', 'entrauchung') },
          ]}
          />
        )}
      >
        {/* TV7a (GO main 20.10.): gegenseitiger Verweis mit dem Bestand (TV8). */}
        <p className="seitenkopf-beschreibung" data-test="verweis-bestand">
          Den Bestand mit AKS-Schlüssel, Anlage und Mengen zeigt{' '}
          <Link to={`/projekte/${projekt.id}/lueftung/brandschutzklappen`}>Brandschutzklappen</Link> (Lüftung → Daten).
        </p>
      </PageHeader>

      {laedt && !daten && <LoadingState text="Brandschutzlisten werden abgerufen …" zeilen={8} />}
      {fehler !== null && (
        <ErrorState
          meldung="Die BSK- und Entrauchungsliste konnten nicht abgerufen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Noch keine BSK- und Entrauchungsliste gerechnet."
          erklaerung="Beide Listen entstehen mit dem Serverwerkzeug lueftung-pruefen aus Lüftungsmodell und Gebäudeautomation. Sobald es gelaufen ist, steht hier jede Klappe mit ihrem Stand."
        />
      )}

      {daten?.lauf && bsk && ent && (
        <>
          <Vorbehalte eintraege={seitenVorbehalte([bsk, ent])} />

          <KpiReihe>
            <KpiCard titel="Brandschutzklappen" wert={bsk.klappen} vergleich={`${formatiereZahl(bsk.motorisch, 0)} motorisch`} />
            <KpiCard titel="BSK konsistent" wert={bsk.konsistent} vergleich={`von ${formatiereZahl(bsk.klappen, 0)}`} />
            <KpiCard
              titel="BSK mit Befund" wert={bsk.mitBefund}
              zustand={bsk.mitBefund > 0 ? 'warnung' : 'gut'} zustandText={bsk.mitBefund > 0 ? 'Befund' : 'keine'}
            />
            <KpiCard titel="Entrauchung" wert={ent.zeilenzahl} vergleich={`${formatiereZahl(ent.anlagen, 0)} Anlagen, ${formatiereZahl(ent.klappen, 0)} Klappen, ${formatiereZahl(ent.zentralen, 0)} Zentralen`} />
            <KpiCard
              titel="Entrauchung mit Befund" wert={ent.mitBefund}
              zustand={ent.mitBefund > 0 ? 'warnung' : 'gut'} zustandText={ent.mitBefund > 0 ? 'Befund' : 'keine'}
            />
          </KpiReihe>

          <ContentSection
            titel="Brandschutzklappen"
            beschreibung="Jede Klappe aus Lüftungsmodell und Gebäudeautomation; Befunde zuerst."
            werkzeuge={<FilterBar filter={bskFilter} werte={bskWerte} beiAenderung={setBskWerte} inLeiste={4} />}
          >
            <DataTable<Bsk>
              name="Brandschutzklappen"
              spalten={bskSpalten}
              zeilen={bskGefiltert}
              schluessel={(z) => String(z.nr)}
              ausfuhr
              ausfuhrDateiname="brandschutzklappen.csv"
              suchePlatzhalter="Element, Anlage, AKS …"
              leer={(
                <EmptyState
                  klein
                  titel={bskZeilen.length === 0 ? 'Der Lauf hat keine Brandschutzklappe gefunden.' : 'Keine Klappe passt zu den Filtern.'}
                  erklaerung={bskZeilen.length === 0
                    ? 'Weder das Lüftungsmodell noch die Gebäudeautomation führen eine Brandschutzklappe nach den Baureihen der Regel bsk_liste.'
                    : 'Die gesetzten Filter schließen alle Klappen aus.'}
                />
              )}
            />
          </ContentSection>

          <ContentSection
            titel="Entrauchung"
            beschreibung="Entrauchungsanlagen, -klappen und RWA-Zentralen; eine Zentrale hat kein Modellelement."
            werkzeuge={<FilterBar filter={entFilter} werte={entWerte} beiAenderung={setEntWerte} inLeiste={3} />}
          >
            <DataTable<Ent>
              name="Entrauchung"
              spalten={entSpalten}
              zeilen={entGefiltert}
              schluessel={(z) => String(z.nr)}
              ausfuhr
              ausfuhrDateiname="entrauchung.csv"
              suchePlatzhalter="Anlage, MSR-Kennung, Element …"
              leer={(
                <EmptyState
                  klein
                  titel={entZeilen.length === 0 ? 'Der Lauf hat keine Entrauchungsanlage gefunden.' : 'Keine Zeile passt zu den Filtern.'}
                  erklaerung={entZeilen.length === 0
                    ? 'Die Gebäudeautomation führt keine Entrauchungsanlage (Gewerk ent) und das Modell keine Entrauchungsklappe.'
                    : 'Die gesetzten Filter schließen alle Zeilen aus.'}
                />
              )}
            />
          </ContentSection>

          <AnnahmenAbschnitt annahmen={[...bsk.annahmen, ...ent.annahmen]} name="Brandschutz" />
          <NormAbschnitt normen={[...bsk.normen, ...ent.normen]} name="Brandschutz" />
        </>
      )}
    </>
  );
}
